import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState, useRef } from "react";
import ErrorPage from "@/pages/-error";
import toast from "react-hot-toast";
import { useDocumentTitle } from "@/hooks/useDocumentTitle.ts";
import { ShieldAlert } from "lucide-react";
import { Button, Spinner } from "@heroui/react";
import { BounceLoader } from "react-spinners";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { asSearchString } from "@repo/utils/search-params";
import { useSmsVerificationWithParam } from "@/service/api/api-with-uid";
import { useAuth } from "@repo/context/auth/auth";

export type F2AOtpSearch = {
  biz?: string;
  redirect?: string;
};

export const Route = createFileRoute("/f2a/otp")({
  validateSearch: (search: Record<string, unknown>): F2AOtpSearch => ({
    biz: asSearchString(search.biz),
    redirect: asSearchString(search.redirect),
  }),
  component: OTP,
});


const routeApi = getRouteApi("/f2a/otp");

export default function OTP() {
  const navigate = useNavigate();
  const { biz, redirect } = routeApi.useSearch();
  const [code, setCode] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [countDown, setCountDown] = useState<number>(0);
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [submitLoading, setSubmitLoading] = useState(false);

  // 使用 useRef 来存储定时器引用，防止内存泄漏
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 使用优化的API工具和用户信息上下文
  const { getSMS, submitSmsCode } = useSmsVerificationWithParam();
  const { userInfo, loading: userLoading } = useAuth();

  useDocumentTitle("两步验证");

  const handleGetSMS = async () => {
    if (countDown > 0) return;
    if (!biz) {
      toast.error("参数无效");
      return;
    }

    try {
      setLoading(true);
      const { status, msg } = await getSMS(biz);

      if (status === 4001) {
        setError(msg);
        return;
      } else if (status !== 0) {
        toast.error(msg);
        return;
      }

      startCountDown();
    } catch (err: any) {
      toast.error(err.msg || '获取验证码失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = useCallback(async () => {
    if (code.length !== 6) {
      toast.error("请输入正确的验证码");
      return;
    }
    if (!biz) {
      toast.error("参数无效");
      return;
    }

    try {
      setSubmitLoading(true);
      const { data } = await submitSmsCode(code, biz);

      if (!data.session_code) return;
      navigate({ href: `${redirect}?session_code=${data.session_code}`, replace: true });
    } catch (err: any) {
      toast.error(err.msg || '提交失败');
    } finally {
      setSubmitLoading(false);
    }
  }, [code, biz, navigate, redirect, submitSmsCode]);

  // 创建一个计时器，当验证码成功发送时，开始倒计时60秒
  const startCountDown = () => {
    // 清除之前的定时器
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setCountDown(60);
    timerRef.current = setInterval(() => {
      setCountDown((pre) => {
        if (pre > 0) {
          return pre - 1;
        } else {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          return 0;
        }
      });
    }, 1000);
  };

  useEffect(() => {
    if (userInfo) {
      // Auth 返回的 Info 在 webview 可能带 country_code
      const phoneNumber = userInfo.phone_number;
      const countryCode = (userInfo as { country_code?: string }).country_code;

      if (phoneNumber && countryCode) {
        // 将手机号转换为+86 133****0000格式，如果是外国手机号，则留第一位和最后一位
        if (countryCode === "86") {
          setPhoneNumber(`+${countryCode} ${phoneNumber.slice(0, 3)}****${phoneNumber.slice(-4)}`);
        } else {
          setPhoneNumber(`+${countryCode} ${phoneNumber.slice(0, 1)}****${phoneNumber.slice(-1)}`);
        }
      } else {
        setPhoneNumber("手机号信息不完整");
      }
    }
  }, [userInfo]);

  // 组件卸载时清理定时器，防止内存泄漏
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  // 显示加载状态
  if (userLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <BounceLoader size={40} color="blue" />
      </div>
    );
  }

  if (error == "用户未登录") {
    return (
      <ErrorPage
        error={{ msg: "登录失效了，请重新开始步骤吧", request_id: "FURRIES.LOGIN.INVALID" }}
      />
    );
  }

  if (!redirect || !biz) {
    return <ErrorPage error={{ msg: "参数无效", request_id: "FURRIES.PARAMS.INVALID" }} />;
  }

  return (
    <div className={"flex flex-col bg-gray-100 h-screen"}>
      <section className={"flex flex-col items-center gap-4 p-8 bg-white"}>
        <div className="p-4 rounded-full bg-green-200/30 mb-4"><ShieldAlert size={100} className="text-green-500/60" strokeWidth={1.5} /></div>
        <span className={"mb-2 text-md"}>为了您的账号安全，我们需要验证您的身份</span>
        <span className={"text-gray-800 text-lg font-bold"}>{phoneNumber || "正在获取手机号..."}</span>
      </section>
      <section className={"flex flex-row items-center p-4 bg-white mt-3"}>
        <span className={"text-md shrink-0 "}>验证码</span>
        <input
          type="number"
          value={code}
          placeholder="请输入验证码"
          onChange={(e) => setCode(e.target.value)}
          className="mx-4 focus:outline-none flex-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
        <div className="min-w-16 flex justify-center">
          {loading ?
            <BounceLoader size={22} color="blue" /> :
            <span onClick={handleGetSMS} className="text-sm shrink-0 text-primary">
              {countDown > 0 ? `重新发送(${countDown}s)` : "获取验证码"}
            </span>
          }
        </div>
      </section>
      <section className={"flex flex-col items-center p-4 mt-1 gap-3"}>
        <Button className="w-full" onPress={() => void handleSubmit()} isPending={submitLoading}>
          {({ isPending }) => (
            <>
              {isPending && <Spinner size="sm" color="current" />}
              确认
            </>
          )}
        </Button>
        <span className="text-sm text-primary" onClick={() => navigate({ to: "/f2a", search: { biz, redirect }, replace: true })}>我无法通过这个方式验证</span>
      </section>
    </div>
  );
}
